import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Icon,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { createOrgInvite, listOrgInvites, revokeOrgInvite } from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import type { OrgInvite, OrgInviteStatus, OrgMemberRole } from "../types.js";

/**
 * "Invitations" card of the organization settings page — the self-service team
 * onboarding surface. An org admin invites a teammate by email at a chosen role;
 * the API returns a one-time accept URL (shown once, like an API key) AND emails
 * it. Pending invites can be revoked. Rendered only for org admins.
 */

const STATUS_COLOR: Record<OrgInviteStatus, "warning" | "success" | "default" | "error"> = {
  pending: "warning",
  accepted: "success",
  revoked: "default",
  expired: "error",
};

export function InvitesCard({ orgId }: { orgId: string }) {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();

  const [invites, setInvites] = useState<OrgInvite[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgMemberRole>("editor");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const roleOptions = (["admin", "editor", "viewer"] as const).map((r) => ({
    label: t(`role.${r}`),
    value: r,
  }));

  const load = async () => {
    setError(null);
    try {
      setInvites(await listOrgInvites(orgId));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
  }, [orgId]);

  const openCreate = () => {
    setEmail("");
    setRole("editor");
    setCreateError(null);
    setCreatedUrl(null);
    setCreateOpen(true);
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("invites.copied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  const submitCreate = async () => {
    if (email.trim() === "") {
      setCreateError(t("invites.emailRequired"));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const result = await createOrgInvite(orgId, { email: email.trim(), role });
      setCreatedUrl(result.acceptUrl);
      toast.success(t("invites.created"));
      await load();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (inviteId: string) => {
    try {
      await revokeOrgInvite(orgId, inviteId);
      toast.success(t("invites.revoked"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <Card
      title={t("invites.title")}
      extra={
        <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
          {t("invites.create")}
        </Button>
      }
    >
      {error != null && <ErrorAlert error={error} />}
      {invites === null ? (
        <Spin />
      ) : (
        <Table
          rowKey={(i) => i.id}
          empty={t("invites.empty")}
          columns={[
            { key: "email", title: t("invites.email"), render: (i: OrgInvite) => i.email },
            {
              key: "role",
              title: t("orgSettings.role"),
              render: (i: OrgInvite) => <Tag>{t(`role.${i.role}`)}</Tag>,
            },
            {
              key: "status",
              title: t("common.status"),
              render: (i: OrgInvite) => (
                <Tag color={STATUS_COLOR[i.status]}>{t(`inviteStatus.${i.status}`)}</Tag>
              ),
            },
            { key: "expiresAt", title: t("invites.expiry"), render: (i: OrgInvite) => fmtDate(i.expiresAt) },
            {
              key: "actions",
              title: t("common.actions"),
              render: (i: OrgInvite) =>
                i.status === "pending" ? (
                  <Popconfirm
                    title={t("invites.revokeConfirm")}
                    okType="danger"
                    okText={t("invites.revoke")}
                    cancelText={t("common.cancel")}
                    onConfirm={() => void revoke(i.id)}
                  >
                    <Button size="sm" variant="danger">
                      {t("invites.revoke")}
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
          data={invites}
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={createdUrl ? t("invites.linkTitle") : t("invites.createTitle")}
        footer={createdUrl ? null : undefined}
        okText={t("invites.send")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={creating}
      >
        {createdUrl ? (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            <Alert type="success">{t("invites.linkReady")}</Alert>
            <Input readOnly value={createdUrl} onFocus={(e) => e.target.select()} />
            <Space size="sm">
              <Button
                variant="primary"
                icon={<Icon name="copy" size={14} />}
                onClick={() => void copy(createdUrl)}
              >
                {t("invites.copy")}
              </Button>
              <Button onClick={() => setCreateOpen(false)}>{t("common.close")}</Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            {createError != null && <ErrorAlert error={createError} />}
            <Text small secondary>
              {t("invites.intro")}
            </Text>
            <Input
              label={t("invites.email")}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <Select
              label={t("orgSettings.role")}
              options={roleOptions}
              value={role}
              onChange={(v) => setRole(v as OrgMemberRole)}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
}
